import React from 'react';
import formatScalar from './formatValue';

/**
 * One filter control, dispatched on its authored `filter_style` (05 §3).
 *
 * Every control here is a pure function of (filter metadata, options, value) —
 * nothing in this file names an entity, a page or a column. The five styles
 * below started as the ones Spells declares, which is deliberately the hardest
 * list page in the app: if the vocabulary survives Spells the rest are subsets.
 * `exclude-term` arrived with Feats, `range` and `select` with Items, and
 * `value-list` with Monsters, and `presence` with Classes' Spellcasting filter
 * (issue 77 — the first Family to declare one as a panel control). `term-list`
 * is what remains, and it lands with the Family that first declares one AS A
 * PANEL CONTROL, so it arrives with a real render to grade it against —
 * Monsters declares a `term-list` but marks it `filter_panel: false`, which is
 * why converting Monsters did not bring one.
 *
 * WHY THERE IS AN "ALL X" OPTION AND WHERE ITS COPY COMES FROM. The shipped
 * pages write their own ("All Schools", "All Sub-schools", "All Domains"), and
 * that copy is UI, not metadata — putting it in the registry would make the
 * authored tree carry English. It is derived from the filter's label here, so a
 * Family gets it for free and the registry stays copy-free. The generated
 * urlParams suite finds controls by the VOCABULARY they offer rather than by
 * their label precisely so this stays a rendering decision.
 */

/** A vocabulary row may be `{name, slug}`, `{name, value}`, or a bare scalar. */
export function optionOf(raw) {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'object') {
    const value = raw.slug ?? raw.value ?? raw.name;
    if (value === undefined || value === null) return null;
    return { value: String(value), label: String(raw.name ?? value) };
  }
  return { value: String(raw), label: String(raw) };
}

export function optionsOf(rows) {
  return (Array.isArray(rows) ? rows : []).map(optionOf).filter(Boolean);
}

/**
 * How an option is SPELLED — the declaring attribute's own number format, on a
 * scalar vocabulary (05 §5's G3, v2.6/v2.7).
 *
 * `ValueList` has done this since R11 so a CR option reads `1/2` and not `0.5`;
 * a `dropdown` over a scalar needs the same rule, and Classes is where it shows:
 * `/filters` answers `[4, 6, 8, 10, 12]` and the shipped page printed `d4`.
 * Routing it through `formatScalar` is what makes the dropdown, the chip, the
 * card and the detail row spell one value one way.
 *
 * A REFERENCE vocabulary is untouched, and the test is structural rather than a
 * flag: a reference row's value is a slug and its label is a name, so the two
 * differ. A scalar's are the same string, which is exactly when the attribute's
 * spelling applies.
 */
export function optionLabel(filter, option) {
  if (option.value !== option.label) return option.label;
  return formatScalar(filter, option.value) || option.label;
}

/**
 * "All Schools", "All Sub-schools", "All Domains", "All Editions" — the shipped
 * copy of every dropdown's empty option, derived from the filter's label rather
 * than authored. It reproduces all seven shipped spellings (School→Schools,
 * Sub-school→Sub-schools, Domain→Domains, Descriptor→Descriptors,
 * Edition→Editions, Rulebook→Rulebooks, and Classes, which already ends in s),
 * and spells Item Category and Weapon Class as English does, where a bare
 * "+s" read "All Item Categorys" and "All Weapon Class".
 */
export function allOptionLabel(label) {
  const text = String(label || '');
  return `All ${pluralOf(text)}`;
}

// English's regular plurals, enough for a filter label: "Category" is
// "Categories", "Class" is "Classes", and a label already plural stays.
function pluralOf(text) {
  if (/(ss|sh|ch|x|z)$/i.test(text)) return `${text}es`;
  if (/s$/i.test(text)) return text;
  if (/[^aeiou]y$/i.test(text)) return `${text.slice(0, -1)}ies`;
  return `${text}s`;
}

function Dropdown({ filter, options, value, onChange }) {
  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="input"
      aria-label={filter.filterLabel}
    >
      <option value="">{allOptionLabel(filter.filterLabel)}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>{optionLabel(filter, option)}</option>
      ))}
    </select>
  );
}

/**
 * The tri-state. Its option VALUES are `''`/`yes`/`no` on every such control, so
 * six of them on one page are indistinguishable to any locator — which is why
 * the generated urlParams suite leaves these to hand-written tests and says so.
 */
function YesNo({ filter, value, onChange }) {
  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="input"
      aria-label={filter.filterLabel}
    >
      <option value="">Any</option>
      <option value="yes">Yes</option>
      <option value="no">No</option>
    </select>
  );
}

/**
 * The `presence` tri-state (issue 77): "has a value / has none", over ANY
 * declared attribute — Classes' Spellcasting filter is the first declaration,
 * on the `spells` reverse section, and nothing here knows that. Like `YesNo`
 * its three choices ARE the style, so it needs no vocabulary from `/filters`;
 * unlike `YesNo` it asks about the attribute having a value at all rather than
 * about a stored boolean. It writes `yes`/`no` (the boolean spellings are read
 * as aliases, exactly as the tri-state's are), and it gets no `_not` control
 * on purpose — "No" already IS the negation of "Yes".
 */
function Presence({ filter, value, onChange }) {
  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="input"
      aria-label={filter.filterLabel}
    >
      <option value="">Any</option>
      <option value="yes">Yes</option>
      <option value="no">No</option>
    </select>
  );
}

function Contains({ filter, value, onChange }) {
  return (
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="input"
      aria-label={filter.filterLabel}
    />
  );
}

/**
 * The CSV multi-select, as a checkbox grid.
 *
 * The options are the values the data actually carries — `/filters` derives
 * them from the links rather than from a hardcoded 0–9 grid (05 §2.2). The
 * checkbox copy is the label's first letter plus the value ("L0".."L9" for
 * Level), which is the shipped spelling derived rather than authored.
 */
function MultiSelect({ filter, options, value, onChange }) {
  const selected = Array.isArray(value) ? value : [];
  // The box copy derives from the ATTRIBUTE's label, not the panel heading:
  // the heading is "Spell Levels" while the boxes read L0..L9.
  const prefix = (filter.label || '').trim().charAt(0).toUpperCase();
  const toggle = (option) => {
    const next = selected.includes(option)
      ? selected.filter((v) => v !== option)
      : [...selected, option].sort((a, b) => Number(a) - Number(b) || String(a).localeCompare(String(b)));
    onChange(next);
  };
  return (
    <div className="level-checkbox-grid">
      {options.map((option) => (
        <label key={option.value} className="page-header cursor-pointer">
          <input
            type="checkbox"
            checked={selected.includes(option.value)}
            onChange={() => toggle(option.value)}
            className="filter-checkbox"
          />
          <span className="filter-checkbox-text">{prefix}{option.label}</span>
        </label>
      ))}
    </div>
  );
}

/**
 * The `exclude-term` toggle (05 §3): a not-contains checkbox over a text
 * attribute, writing `?<url_key>=true`.
 *
 * The Eberron-ness lives entirely in the authored term and label — the shipped
 * "Exclude Dragonmark feats" box is `{ attr: "name", term: "Dragonmark" }` and
 * nothing here knows what a dragonmark is. It is the one control that is its own
 * label rather than a control under one, which is why it opts out of
 * `FilterControl`'s wrapper below: a second `<label>` carrying the same copy
 * would make the checkbox ambiguous to any locator.
 */
function ExcludeTerm({ filter, value, onChange }) {
  return (
    <label className="filter-checkbox-label">
      <input
        type="checkbox"
        checked={value === 'true'}
        onChange={(e) => onChange(e.target.checked ? 'true' : '')}
        className="filter-checkbox"
      />
      <span className="filter-checkbox-text">{filter.filterLabel}</span>
    </label>
  );
}

/**
 * The bucket vocabulary of a `range` filter, derived from its authored
 * BOUNDARIES — the same four numbers, the same six options, as
 * `rangeBuckets` in `server/routes/generic.js`.
 *
 * The two derivations have to agree because one writes the URL and the other
 * reads it; they are kept as one RULE stated twice rather than one authored
 * list, because the boundaries are the data and the option spellings
 * (`under_1000`, `25000_plus`) are a shipped URL contract. A test asserts the
 * two agree over the authored numbers.
 *
 * The labels carry the attribute's unit, so the same `unit: "gp"` that makes a
 * card read `2,500 gp` makes this option read `Under 1,000 gp` — one authored
 * fact, every context (gap G3).
 */
export function rangeOptions(filter) {
  const bounds = (filter.filterValues || []).map(Number).filter((n) => Number.isFinite(n));
  if (!bounds.length) return [];
  const unit = filter.unit ? ` ${filter.unit}` : '';
  const n = (v) => v.toLocaleString();
  const out = [{ value: `under_${bounds[0]}`, label: `Under ${n(bounds[0])}${unit}` }];
  for (let i = 0; i < bounds.length - 1; i += 1) {
    out.push({
      value: `${bounds[i]}_${bounds[i + 1]}`,
      label: `${n(bounds[i])} - ${n(bounds[i + 1])}${unit}`,
    });
  }
  const last = bounds[bounds.length - 1];
  out.push({ value: `${last}_plus`, label: `${n(last)}+${unit}` });
  out.push({ value: 'custom', label: 'Custom Range' });
  return out;
}

/**
 * The `range` control: the bucket select, plus the two custom bounds it reveals.
 *
 * The bounds are their own URL parameters (`?min_price=`, `?max_price=`), keyed
 * off the ATTRIBUTE key rather than the filter's `url_key`, which is the shipped
 * contract — the select writes `?price_filter=` and the inputs write
 * `?min_price=`/`?max_price=`. They are written per keystroke, as today (05 §3,
 * open question 4: debouncing is a real behavior change and is deferred).
 */
function Range({ filter, value, onChange, extra, onExtraChange }) {
  const options = rangeOptions(filter);
  return (
    <>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="input w-full"
        aria-label={filter.filterLabel}
      >
        <option value="">Any {filter.filterLabel}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
      {value === 'custom' && (
        <div className="mt-3 flex gap-4">
          {[['min', 'Min'], ['max', 'Max']].map(([bound, word]) => (
            <div className="flex-1" key={bound}>
              <label className="filter-label">
                {word} {filter.filterLabel}{filter.unit ? ` (${filter.unit})` : ''}
              </label>
              <input
                type="number"
                value={(extra || {})[`${bound}_${filter.key}`] || ''}
                onChange={(e) => onExtraChange(`${bound}_${filter.key}`, e.target.value)}
                className="input"
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/**
 * The `select` style: an exclusionary default with an explicit show-all escape
 * (05 §3, `property_filter`).
 *
 * It renders as an ordinary dropdown; what makes it a style of its own is the
 * URL contract behind it, which lives in `useListQueryState` — the default is
 * represented by ABSENCE in the URL so a pristine link and a cleared one mean
 * the same thing, and `filter_unfiltered_values` sit in state and on the URL
 * without ever reaching the API. There is no empty option, because a non-empty
 * default means "no filter" is not one of the choices.
 */
function Select({ filter, options, value, onChange }) {
  return (
    <select
      value={value || filter.filterDefault || ''}
      onChange={(e) => onChange(e.target.value)}
      className="input w-full"
      aria-label={filter.filterLabel}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>{option.label}</option>
      ))}
    </select>
  );
}

/**
 * A dropdown over an AUTHORED vocabulary, formatted by the declaring
 * attribute's own number format (05 §3, v2.6's gap G3).
 *
 * Monsters is the Family that declares one: `challenge_rating` carries
 * `filter_values` — the 36 CRs the shipped page hardcoded in
 * `client/src/utils/formatCR.js` — and `format: "fraction"`, so the option for
 * 0.5 must read `1/2` and not `0.5`. Routing it through `formatScalar` is what
 * makes that one rule rather than a second fraction table beside the badge's:
 * an attribute's number is spelled the same way wherever it appears.
 *
 * `term-list`, the other style Monsters declares, deliberately has NO control
 * here. Its one declaration (`environment`) is `filter_panel: false` —
 * filterable and read by the encounter generator, never a control on the
 * Monsters list — so a control would be speculative code with no render to
 * grade it against.
 */
function ValueList({ filter, value, onChange }) {
  return (
    <select
      value={value === '' || value === undefined ? '' : String(value)}
      onChange={(e) => onChange(e.target.value)}
      className="input"
      aria-label={filter.filterLabel}
    >
      <option value="">{allOptionLabel(filter.filterLabel)}</option>
      {(filter.filterValues || []).map((raw) => (
        <option key={String(raw)} value={String(raw)}>{formatScalar(filter, raw)}</option>
      ))}
    </select>
  );
}

/**
 * The derived advanced-filter affordances (issue 187). Eligibility is read off
 * the registry rather than hand-picked, mirroring the server's own derivation:
 *
 *  - `hasDerivedBounds`: any declared filter over a `number` attribute gets a
 *    Min/Max pair writing `min_<url_key>`/`max_<url_key>` (the `range` style
 *    keeps its own custom-span inputs, keyed off the attribute — the shipped
 *    Items contract).
 *  - `isNegatable`: any single-attribute `dropdown` gets an exclude control
 *    writing `<url_key>_not`. A group dropdown ranges over its MEMBERS, so
 *    "not" has no single-field meaning there; `value-list` accepts the
 *    parameter server-side but offers no control (excluding one CR from 36 is
 *    a bound wearing a costume); `presence` gets neither the control nor the
 *    parameter, because its own "No" already IS the negation of its "Yes".
 */
export function boundParamBase(filter) {
  return filter.style === 'range' ? filter.key : filter.urlKey;
}

export function hasDerivedBounds(filter) {
  return filter.datatype === 'number'
    && ['dropdown', 'value-list', 'multi-select'].includes(filter.style)
    && filter.where !== 'group';
}

export function isNegatable(filter) {
  return filter.style === 'dropdown' && (filter.where === undefined || filter.where === 'attribute');
}

/**
 * The Min/Max pair of a numeric filter — two more URL parameters riding beside
 * the control's own, exactly as the `range` style's custom span always has.
 */
function NumericBounds({ filter, extra, onExtraChange }) {
  const base = boundParamBase(filter);
  return (
    <div className="mt-2 flex gap-4">
      {[['min', 'Min'], ['max', 'Max']].map(([bound, word]) => (
        <input
          key={bound}
          type="number"
          placeholder={word}
          aria-label={`${word} ${filter.filterLabel}`}
          value={(extra || {})[`${bound}_${base}`] || ''}
          onChange={(e) => onExtraChange(`${bound}_${base}`, e.target.value)}
          className="input flex-1"
        />
      ))}
    </div>
  );
}

/**
 * The exclude control of a negatable dropdown: the same vocabulary, negated —
 * `?rulebook_not=phb` is "feats NOT from the PHB". Its own URL parameter, so
 * including one value and excluding another are independent choices.
 */
function ExcludeSelect({ filter, options, extra, onExtraChange }) {
  const key = `${filter.urlKey}_not`;
  return (
    <select
      value={(extra || {})[key] || ''}
      onChange={(e) => onExtraChange(key, e.target.value)}
      className="input mt-2"
      aria-label={`Exclude ${filter.filterLabel}`}
    >
      <option value="">No exclusions</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          Not {optionLabel(filter, option)}
        </option>
      ))}
    </select>
  );
}

const STYLES = {
  dropdown: Dropdown,
  'value-list': ValueList,
  'yes-no': YesNo,
  presence: Presence,
  contains: Contains,
  'multi-select': MultiSelect,
  'exclude-term': ExcludeTerm,
  range: Range,
  select: Select,
};

/** Styles that ARE their own label and take no `filter-label` wrapper. */
const SELF_LABELLING = new Set(['exclude-term']);

/** The styles this slice renders — asserted by a test, like IMPLEMENTED_STYLES. */
export const IMPLEMENTED_FILTER_STYLES = new Set(Object.keys(STYLES));

export default function FilterControl({
  filter, options, value, onChange, extra, onExtraChange,
}) {
  const Control = STYLES[filter.style];
  if (!Control) return null;
  // A control with nothing to offer is not rendered: an empty dropdown is a
  // filter that cannot do anything, which is exactly the three inert Items
  // controls allowlist A30 removes.
  if ((filter.style === 'dropdown' || filter.style === 'multi-select' || filter.style === 'select')
      && !options.length) return null;
  if (filter.style === 'value-list' && !(filter.filterValues || []).length) return null;
  if (filter.style === 'range' && !rangeOptions(filter).length) return null;
  if (SELF_LABELLING.has(filter.style)) {
    return (
      <div className="flex items-center">
        <Control filter={filter} options={options} value={value} onChange={onChange} />
      </div>
    );
  }
  return (
    <div>
      <label className="filter-label">{filter.filterLabel}</label>
      <Control
        filter={filter}
        options={options}
        value={value}
        onChange={onChange}
        extra={extra}
        onExtraChange={onExtraChange}
      />
      {hasDerivedBounds(filter) && (
        <NumericBounds filter={filter} extra={extra} onExtraChange={onExtraChange} />
      )}
      {isNegatable(filter) && options.length > 0 && (
        <ExcludeSelect filter={filter} options={options} extra={extra} onExtraChange={onExtraChange} />
      )}
    </div>
  );
}
